<template>
  <div class="parking-space-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('listParkingSpace.page.title')" :subtitle="$t('listParkingSpace.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="openBatchAddParkingSpaceModal">
          {{ $t('listParkingSpace.batchAdd') }}
        </el-button>
        <el-button icon="el-icon-plus" @click="openAddParkingSpaceModal">
          {{ $t('listParkingSpace.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="6" :lg="5">
          <el-input v-model="listParkingSpaceInfo.conditions.areaNum" clearable class="w-full"
            :placeholder="$t('listParkingSpace.parkingLotPlaceholder')" @keyup.enter.native="queryParkingSpaceMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="6" :lg="3">
          <el-button icon="el-icon-search" @click="openChooseParkingArea">
            {{ $t('listParkingSpace.select') }}
          </el-button>
        </el-col>
        <el-col :xs="24" :sm="12" :md="6" :lg="5">
          <el-input v-model.trim="listParkingSpaceInfo.num" clearable class="w-full"
            :placeholder="$t('listParkingSpace.parkingSpaceNumPlaceholder')"
            @keyup.enter.native="queryParkingSpaceMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="6" :lg="5">
          <el-select v-model="listParkingSpaceInfo.conditions.state" clearable class="w-full"
            :placeholder="$t('listParkingSpace.parkingSpaceStatePlaceholder')">
            <el-option value="" :label="$t('listParkingSpace.parkingSpaceStatePlaceholder')" />
            <el-option value="S" :label="$t('listParkingSpace.forSale')" />
            <el-option value="H" :label="$t('listParkingSpace.forRent')" />
            <el-option value="F" :label="$t('listParkingSpace.free')" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="6" :lg="4">
          <el-input v-model.trim="listParkingSpaceInfo.conditions.psId" clearable class="w-full"
            :placeholder="$t('listParkingSpace.parkingSpaceIdPlaceholder')"
            @keyup.enter.native="queryParkingSpaceMethod" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="queryParkingSpaceMethod">
          {{ $t('listParkingSpace.query') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetParkingSpaceMethod">
          {{ $t('listParkingSpace.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="listParkingSpaceInfo.total" :density="density" :show-column-setting="false"
        :show-refresh="true" @update:density="density = $event" @refresh="loadParkingSpaceData">
        <template #left>
          <span class="ds-panel__title">{{ $t('listParkingSpace.parkingSpaceInfo') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="listParkingSpaceInfo.parkingSpaces" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border :empty-description="$t('listParkingSpace.page.empty')"
        @retry="loadParkingSpaceData">
        <el-table-column prop="areaNum" :label="$t('listParkingSpace.parkingLot')" align="center" width="140" />
        <el-table-column prop="num" :label="$t('listParkingSpace.parkingSpace')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.num || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('listParkingSpace.parkingSpaceState')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag size="sm" :type="resolveParkingSpaceState(scope.row.state).type"
              :label="resolveParkingSpaceState(scope.row.state).label" />
          </template>
        </el-table-column>
        <el-table-column prop="parkingTypeName" :label="$t('listParkingSpace.parkingSpaceType')" align="center"
          min-width="120" />
        <el-table-column prop="area" :label="$t('listParkingSpace.area')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.area || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('listParkingSpace.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('listParkingSpace.operation')" align="center" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="openEditParkingSpaceModel(scope.row)">
              {{ $t('listParkingSpace.modify') }}
            </el-button>
            <el-button size="mini" type="danger" @click="openDelParkingSpaceModel(scope.row)">
              {{ $t('listParkingSpace.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :page="listParkingSpaceInfo.currentPage" :size="listParkingSpaceInfo.pageSize"
        :total="listParkingSpaceInfo.total" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handlePageChange" />
    </div>

    <!-- 子组件 -->
    <add-parking-space ref="addParkingSpace" @success="handleAddSuccess" />
    <edit-parking-space ref="editParkingSpace" @success="handleEditSuccess" />
    <delete-parking-space ref="deleteParkingSpace" @success="handleDeleteSuccess" />
    <choose-parking-area ref="chooseParkingArea" @choose="handleChooseParkingArea" />
    <batch-add-parking-space ref="batchAddParkingSpace" @success="handleBatchAddSuccess" />
  </div>
</template>

<script>
import { listParkingSpaces } from '@/api/car/listParkingSpaceApi'
import { getCommunityId } from '@/api/community/communityApi'
import AddParkingSpace from '@/components/car/addParkingSpace'
import EditParkingSpace from '@/components/car/editParkingSpace'
import DeleteParkingSpace from '@/components/car/deleteParkingSpace'
import ChooseParkingArea from '@/components/car/chooseParkingArea'
import BatchAddParkingSpace from '@/components/car/batchAddParkingSpace'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ListParkingSpace',
  components: {
    AddParkingSpace,
    EditParkingSpace,
    DeleteParkingSpace,
    ChooseParkingArea,
    BatchAddParkingSpace,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      listParkingSpaceInfo: {
        parkingSpaces: [],
        total: 0,
        currentPage: 1,
        pageSize: 10,
        num: '',
        moreCondition: false,
        conditions: {
          psId: '',
          area: '',
          paId: '',
          areaNum: '',
          state: ''
        }
      }
    }
  },
  created() {
    this.loadParkingSpaceData()
  },
  methods: {
    async loadParkingSpaceData() {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          page: this.listParkingSpaceInfo.currentPage,
          row: this.listParkingSpaceInfo.pageSize,
          communityId: getCommunityId(),
          num: this.listParkingSpaceInfo.num,
          psId: this.listParkingSpaceInfo.conditions.psId,
          area: this.listParkingSpaceInfo.conditions.area,
          paId: this.listParkingSpaceInfo.conditions.paId,
          state: this.listParkingSpaceInfo.conditions.state
        }

        const res = await listParkingSpaces(params)
        this.listParkingSpaceInfo.parkingSpaces = res.parkingSpaces
        this.listParkingSpaceInfo.total = res.total
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('common.loadError')
        this.$message.error(this.$t('common.loadError'))
      } finally {
        this.loading = false
      }
    },

    resolveParkingSpaceState(state) {
      if (state === 'S') return { type: 'warning', label: this.$t('listParkingSpace.forSale') }
      if (state === 'H') return { type: 'primary', label: this.$t('listParkingSpace.forRent') }
      if (state === 'F') return { type: 'success', label: this.$t('listParkingSpace.free') }
      return { type: 'muted', label: 'Unknown' }
    },

    viewParkingSpaceState(state) {
      return this.resolveParkingSpaceState(state).label
    },

    toggleMoreCondition() {
      this.listParkingSpaceInfo.moreCondition = !this.listParkingSpaceInfo.moreCondition
    },

    queryParkingSpaceMethod() {
      this.listParkingSpaceInfo.currentPage = 1
      this.loadParkingSpaceData()
    },

    resetParkingSpaceMethod() {
      this.listParkingSpaceInfo.conditions = {
        psId: '',
        area: '',
        paId: '',
        areaNum: '',
        state: ''
      }
      this.listParkingSpaceInfo.num = ''
      this.listParkingSpaceInfo.currentPage = 1
      this.loadParkingSpaceData()
    },

    openChooseParkingArea() {
      this.$refs.chooseParkingArea.open()
    },

    openAddParkingSpaceModal() {
      this.$refs.addParkingSpace.open()
    },

    openEditParkingSpaceModel(row) {
      this.$refs.editParkingSpace.open(row)
    },

    openDelParkingSpaceModel(row) {
      this.$refs.deleteParkingSpace.open(row)
    },

    openBatchAddParkingSpaceModal() {
      this.$refs.batchAddParkingSpace.open()
    },

    handleChooseParkingArea(parkingArea) {
      this.listParkingSpaceInfo.conditions.paId = parkingArea.paId
      this.listParkingSpaceInfo.conditions.areaNum = parkingArea.num
    },

    handleAddSuccess() {
      this.loadParkingSpaceData()
    },

    handleEditSuccess() {
      this.loadParkingSpaceData()
    },

    handleDeleteSuccess() {
      this.loadParkingSpaceData()
    },

    handleBatchAddSuccess() {
      this.loadParkingSpaceData()
    },

    handlePageChange(page) {
      this.listParkingSpaceInfo.currentPage = page
      this.loadParkingSpaceData()
    },

    handleSizeChange(size) {
      this.listParkingSpaceInfo.pageSize = size
      this.loadParkingSpaceData()
    }
  }
}
</script>

<style lang="scss" scoped>
.parking-space-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
